import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Star } from "lucide-react";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { PROVIDERS, findModelLabel } from "@/lib/providers";
import { gegroepeerd, LOKAAL } from "@/lib/aanbieder-locatie";
import { pastTekst, grootteTekst } from "./MachineAdvies";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";

/**
 * MK2 (GO Frank 3 november 2026): de modelkiezer in het invoervak, naast Plus.
 *
 * Bovenaan het lokaal-advies voor deze Mac (MK1), daaronder een zoekveld en de favorieten, dan de modellen per plek
 * (lokaal, EU, buiten de EU; binnen een plek per aanbieder), en onderaan "Standaard uit Instellingen", "Modellen
 * verversen" en "Eigen model toevoegen". De keuze geldt voor DIT gesprek (besluit Frank). Een cloudaanbieder staat er
 * alleen in als er een sleutel voor is; de plek komt uit de ene tabel (`lib/aanbieder-locatie.js`), de lokale modellen
 * uit de catalogus van MK1. Er wordt niets gedownload.
 */

export const SCHEIDING = "|";
export const favorietVan = (aanbieder, model) => `${aanbieder}${SCHEIDING}${model}`;

/**
 * De groepen van de kiezer. PUUR. `lokaal` is de lijst uit de catalogus, `sleutels` de aanbieders met een sleutel.
 * De zoekterm filtert op naam en aanbieder; de favorieten staan als eigen groep bovenaan en blijven ook in hun plek.
 */
export function groepenVoor({ lokaal = [], sleutels = [], favorieten = [], zoek = "", aanbieders = PROVIDERS,
                              eigenLocatie = "" }) {
  const term = zoek.trim().toLowerCase();
  const past = (item) => !term || item.label.toLowerCase().includes(term) || item.model.toLowerCase().includes(term)
    || item.naamAanbieder.toLowerCase().includes(term);
  const favoriet = new Set(favorieten);
  const bruikbaar = Object.fromEntries(Object.entries(aanbieders).filter(([k]) => k === "ollama" || sleutels.includes(k)));
  const plekken = gegroepeerd(bruikbaar, eigenLocatie).map((g) => ({
    plek: g.plek,
    tekst: g.tekst,
    items: g.items.flatMap(([aanbieder, p]) => {
      const modellen = aanbieder === "ollama"
        ? lokaal.filter((m) => !(m.mogelijkheden || []).length || (m.mogelijkheden || []).includes("completion"))
          .map((m) => ({ value: m.naam, label: m.naam, past: m.past }))
        : p.models || [];
      return modellen.map((m) => ({ aanbieder, model: m.value, label: m.label, naamAanbieder: p.name,
                                    past: m.past, favoriet: favoriet.has(favorietVan(aanbieder, m.value)) }));
    }).filter(past),
  })).filter((g) => g.items.length > 0);
  const favs = plekken.flatMap((g) => g.items).filter((i) => i.favoriet);
  return favs.length ? [{ plek: "favorieten", tekst: "Favorieten", items: favs }, ...plekken] : plekken;
}

/** MK4: de woorden van de denkniveaus, zoals de kiezer ze toont (de server bewaart de kleine letters). */
export const NIVEAU_LABEL = { uit: "Uit", laag: "Laag", gemiddeld: "Gemiddeld", hoog: "Hoog", aan: "Aan" };

/**
 * MK4: de denkniveaus die het model van DIT gesprek echt kent: uit de catalogus (backend, `services/denkniveau.py`),
 * dus geen tweede regel hier. Het model is de keuze van het gesprek, anders de standaard uit Instellingen. Een
 * cloudmodel krijgt geen niveaus: NovaMund vertaalt ze daar nog niet. PUUR.
 */
export function niveausVoor(keuze, standaard, lokaal = []) {
  const aanbieder = keuze?.aanbieder || standaard?.provider;
  const model = keuze?.aanbieder ? keuze.model : standaard?.model;
  if (aanbieder !== "ollama" || !model) return [];
  return (lokaal.find((m) => m.naam === model)?.niveaus) || [];
}

/** De tekst op de knop: het gekozen model, of het model uit Instellingen met "standaard" erbij. PUUR. */
export function knopTekst(keuze, standaard) {
  if (keuze?.aanbieder) return keuze.model ? findModelLabel(keuze.aanbieder, keuze.model) : PROVIDERS[keuze.aanbieder]?.name || keuze.aanbieder;
  return standaard?.model ? findModelLabel(standaard.provider, standaard.model) : "";
}

export default function ModelKiezer({ keuze, standaard, onKies, onKiesNiveau, onEigenModel, className = "" }) {
  const { t } = useTaal();
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState("");
  const [catalogus, setCatalogus] = useState(null);
  const [sleutels, setSleutels] = useState([]);
  const [favorieten, setFavorieten] = useState([]);

  const laad = useCallback(async () => {
    const [cat, keys, inst] = await Promise.allSettled([
      axios.get(`${API}/ollama/catalogus`), axios.get(`${API}/keys`), axios.get(`${API}/settings`)]);
    setCatalogus(cat.status === "fulfilled" ? cat.value.data : null);
    setSleutels(keys.status === "fulfilled" && Array.isArray(keys.value.data)
      ? [...new Set(keys.value.data.map((k) => k.provider))] : []);
    setFavorieten(inst.status === "fulfilled" ? inst.value.data.model_favorieten || [] : []);
  }, []);

  useEffect(() => { if (open) laad(); }, [open, laad]);

  const groepen = useMemo(() => groepenVoor({ lokaal: catalogus?.lokaal || [], sleutels, favorieten, zoek,
                                              eigenLocatie: standaard?.eigenLocatie || "" }),
                          [catalogus, sleutels, favorieten, zoek, standaard]);

  async function kies(aanbieder, model) {
    try {
      await onKies?.(aanbieder, model);
      setOpen(false);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("Het model kon niet worden gekozen."));
    }
  }

  async function wisselFavoriet(item) {
    const sleutel = favorietVan(item.aanbieder, item.model);
    const nieuw = favorieten.includes(sleutel) ? favorieten.filter((f) => f !== sleutel) : [...favorieten, sleutel];
    try {
      await axios.put(`${API}/settings`, { model_favorieten: nieuw });
      setFavorieten(nieuw);
    } catch {
      toast.error(t("Niet opgeslagen"));
    }
  }

  const gekozen = (i) => keuze?.aanbieder === i.aanbieder && (keuze?.model || "") === i.model;
  const advies = catalogus?.advies;
  const niveaus = niveausVoor(keuze, standaard, catalogus?.lokaal || []);

  async function kiesNiveau(niveau) {
    try {
      await onKiesNiveau?.(niveau);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("Het denkniveau kon niet worden gekozen."));
    }
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="modelkiezer" title={t("Model voor dit gesprek")}
                className={`inline-flex max-w-[11rem] items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted ${className}`}>
          <span className="truncate" data-testid="modelkiezer-tekst">
            {knopTekst(keuze, standaard) || t("Model")}{!keuze?.aanbieder && <span className="opacity-70">{" · "}{t("standaard")}</span>}
            {keuze?.denkniveau && <span data-testid="modelkiezer-niveau">{" · "}{t(NIVEAU_LABEL[keuze.denkniveau] || keuze.denkniveau)}</span>}
          </span>
          <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 max-h-[70vh] overflow-y-auto p-2 text-sm" data-testid="modelkiezer-menu">
        {catalogus?.machine && (
          <p className="px-1 pb-2 text-xs text-muted-foreground" data-testid="modelkiezer-advies">
            {t("Deze Mac: {chip}, {gb} GB", { chip: catalogus.machine.chip || t("onbekend"), gb: catalogus.machine.geheugen_gb || "?" })}
            {advies && <>{". "}{t("Advies voor deze Mac: {model}", { model: advies.model })}
              {advies.grootte_gb != null && ` (${grootteTekst(advies.grootte_gb)})`}{", "}{t(pastTekst(advies.past))}
              {!advies.geinstalleerd && <>{", "}{t("niet geinstalleerd")}</>}</>}
          </p>
        )}
        {niveaus.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-1 px-1" data-testid="modelkiezer-niveaus">
            <span className="text-xs text-muted-foreground">{t("Denkniveau")}:</span>
            {[...niveaus, ""].map((n) => (
              <button key={n || "standaard"} type="button" onClick={() => kiesNiveau(n)}
                      aria-pressed={(keuze?.denkniveau || "") === n} data-testid={`niveau-${n || "standaard"}`}
                      className={`rounded px-1.5 py-0.5 text-xs hover:bg-muted ${(keuze?.denkniveau || "") === n ? "font-semibold underline" : ""}`}>
                {n ? t(NIVEAU_LABEL[n]) : t("Standaard")}
              </button>
            ))}
          </div>
        )}
        <Input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder={t("Zoek een model")}
               className="mb-2 h-8 text-sm" data-testid="modelkiezer-zoek" />
        {groepen.map((g) => (
          <div key={g.plek} className="mb-2" data-testid={`modelkiezer-groep-${g.plek}`}>
            <p className="px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t(g.tekst)}</p>
            {g.items.map((i) => (
              <div key={`${g.plek}-${i.aanbieder}-${i.model}`} className="flex items-center gap-1">
                <button type="button" onClick={() => kies(i.aanbieder, i.model)}
                        data-testid={`model-${i.aanbieder}-${i.model}`} aria-pressed={gekozen(i)}
                        className={`flex-1 truncate rounded px-1 py-1 text-left hover:bg-muted ${gekozen(i) ? "font-semibold" : ""}`}>
                  {i.label}
                  {g.plek !== LOKAAL && g.plek !== "favorieten" ? null : i.past === false
                    ? <span className="ml-1 text-xs text-muted-foreground">{t("past niet")}</span> : null}
                </button>
                <button type="button" onClick={() => wisselFavoriet(i)} aria-pressed={i.favoriet}
                        aria-label={i.favoriet ? t("Uit favorieten") : t("Naar favorieten")}
                        data-testid={`favoriet-${i.aanbieder}-${i.model}`} className="rounded p-1 hover:bg-muted">
                  <Star className={`h-3 w-3 ${i.favoriet ? "fill-current" : ""}`} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        ))}
        {groepen.length === 0 && <p className="px-1 py-2 text-xs text-muted-foreground">{t("Geen model gevonden")}</p>}
        <div className="mt-1 border-t pt-1">
          <button type="button" onClick={() => kies(null, null)} data-testid="modelkiezer-standaard"
                  className="block w-full rounded px-1 py-1 text-left hover:bg-muted">{t("Standaard uit Instellingen")}</button>
          <button type="button" onClick={laad} data-testid="modelkiezer-verversen"
                  className="block w-full rounded px-1 py-1 text-left hover:bg-muted">{t("Modellen verversen")}</button>
          <button type="button" onClick={() => { setOpen(false); onEigenModel?.(); }} data-testid="modelkiezer-eigen"
                  className="block w-full rounded px-1 py-1 text-left hover:bg-muted">{t("Eigen model toevoegen")}</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
